import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inviteAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, ShieldAlert, Mail, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_META = {
  user: { label: 'User', icon: Sparkles, color: 'bg-zinc-700/30 text-zinc-300 border-zinc-700' },
  admin: { label: 'Admin', icon: ShieldCheck, color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  super_admin: { label: 'Super Admin', icon: ShieldAlert, color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await inviteAPI.get(token);
        setInvite(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invite is invalid or expired');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await inviteAPI.accept(token, { name, password });
      localStorage.setItem('clippost_token', res.data.token);
      localStorage.setItem('clippost_user', JSON.stringify(res.data.user));
      await refreshUser();
      toast.success(`Welcome to ClipParrot, ${res.data.user.name}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-error-page">
        <Card className="max-w-md w-full bg-zinc-950/60 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading">Invite unavailable</CardTitle>
            <CardDescription>{error || 'This invite link is no longer valid.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')} data-testid="invite-go-login-button">
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const role = ROLE_META[invite.role] || ROLE_META.user;
  const RoleIcon = role.icon;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.08),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(168,85,247,0.06),transparent_50%)] pointer-events-none" />
      <Card className="relative max-w-md w-full bg-zinc-950/70 border-white/10 backdrop-blur-xl">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <img src="/logo-white-wide.png" alt="ClipParrot" className="h-9 object-contain" />
          </div>
          <div className="pt-2">
            <CardTitle className="font-heading text-2xl tracking-tight">You're invited</CardTitle>
            <CardDescription className="mt-1.5">
              {invite.invited_by_name || 'A ClipParrot admin'} invited you to join as a{' '}
              <span className="text-foreground font-medium">{role.label.toLowerCase()}</span>.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="outline" className="border-white/10 text-muted-foreground gap-1.5">
              <Mail className="w-3 h-3" />
              {invite.email}
            </Badge>
            <Badge className={`${role.color} gap-1.5`}>
              <RoleIcon className="w-3 h-3" />
              {role.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Your name</label>
              <Input
                className="mt-1.5 bg-zinc-900/60 border-white/10"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                required
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Set a password</label>
              <Input
                className="mt-1.5 bg-zinc-900/60 border-white/10"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                minLength={8}
                required
                data-testid="invite-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="invite-accept-button">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Accept invite & create account
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
